"use client";

import { useEffect, useId, useRef, useState } from "react";

import LinkWithLang, {
  type LinkRouteDefaults,
} from "@stadium/shared/src/components/link-with-lang";
import Multimedia from "@stadium/shared/src/components/shared/multimedia";

import { CATALOG_ENTRIES_DEFAULTS, CE_11 } from "@stadium/shared/src/helpers/plasmic/static-slices";

import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

import {
  FILTER_OPTIONS_VALUES,
  FilterOptionValue,
  LinkWithLangType,
} from "@stadium/shared/src/constants/stores/routes";

import styles from "./main.module.scss";

gsap.registerPlugin(ScrollTrigger);

const HIGHLIGHTED_COLORS = {
  word1: "#ffb800",
  word2: "#EA6779",
  word3: "#3678F2",
} as const;

const DEFAULT_FILTER_QUERY = {};

export interface GridItem {
  image?: any;
  imageAlt?: string;
  linkText?: string;
  linkType?: LinkWithLangType;
  linkOpenInNewTab?: boolean;
  linkOtherUrl?: string;
  linkCategorySlug?: string;
  filterOptions?: FilterOptionValue;
}

export interface CE11Props {
  eyebrowText?: string;
  mainHeading?: string;
  highlightedWord1?: string;
  highlightedWord2?: string;
  highlightedWord3?: string;
  subHeading?: string;
  description?: string;
  ctaText?: string;
  ctaLinkType?: LinkWithLangType;
  ctaOpenInNewTab?: boolean;
  ctaOtherUrl?: string;
  ctaCategorySlug?: string;
  gridItem1?: GridItem;
  gridItem2?: GridItem;
  gridItem3?: GridItem;
  gridItem4?: GridItem;
  gridItem5?: GridItem;
  gridItem6?: GridItem;
  gridItem7?: GridItem;
  gridItem8?: GridItem;
  gridItem9?: GridItem;
  className?: string;
  linkRouteDefaults?: LinkRouteDefaults;
}

interface GridItemCardProps {
  item: GridItem;
  imageWrapperClass: string;
  linkWrapperClass: string;
  linkRouteDefaults?: LinkRouteDefaults;
}

const GridItemCard: React.FC<Readonly<GridItemCardProps>> = ({
  item,
  imageWrapperClass,
  linkWrapperClass,
  linkRouteDefaults,
}) => {
  const query = item.filterOptions
    ? FILTER_OPTIONS_VALUES[item.filterOptions]
    : DEFAULT_FILTER_QUERY;

  return (
    <LinkWithLang
      {...linkRouteDefaults}
      type={item.linkType}
      data={{ code: item.linkCategorySlug, url: item.linkOtherUrl }}
      target={item.linkOpenInNewTab ? "_blank" : "_self"}
      className={`${imageWrapperClass} ${styles.cardLinkWrapper}`}
      query={query}
    >
      <Multimedia type="image" src={item.image} alt={item.imageAlt} className={styles.imageInner} />

      <div className={linkWrapperClass}>
        <div className={styles.linkGroup}>
          <span className={styles.linkPill}>
            {item.linkText?.length > 15 ? `${item.linkText.slice(0, 15)}` : item.linkText}
          </span>
          <span className={styles.linkCircle}></span>
        </div>
      </div>
    </LinkWithLang>
  );
};

// Reconstruct gridItems array from flattened static-slices data
const ce11Defaults = CATALOG_ENTRIES_DEFAULTS[CE_11];

const CE11: React.FC<Readonly<CE11Props>> = ({
  eyebrowText = ce11Defaults.eyebrowText,
  mainHeading = ce11Defaults.mainHeading,
  highlightedWord1 = ce11Defaults.highlightedWord1,
  highlightedWord2 = ce11Defaults.highlightedWord2,
  highlightedWord3 = ce11Defaults.highlightedWord3,
  subHeading = ce11Defaults.subHeading,
  description = ce11Defaults.description,
  ctaText = ce11Defaults.ctaText,
  ctaLinkType = ce11Defaults.ctaLinkType,
  ctaOpenInNewTab = false,
  ctaOtherUrl = ce11Defaults.ctaOtherUrl,
  ctaCategorySlug = ce11Defaults.ctaCategorySlug,
  gridItem1 = {
    image: ce11Defaults.gridItem1Image,
    imageAlt: ce11Defaults.gridItem1ImageAlt,
    linkText: ce11Defaults.gridItem1LinkText,
    linkUrl: ce11Defaults.gridItem1LinkUrl,
  },
  gridItem2 = {
    image: ce11Defaults.gridItem2Image,
    imageAlt: ce11Defaults.gridItem2ImageAlt,
    linkText: ce11Defaults.gridItem2LinkText,
    linkUrl: ce11Defaults.gridItem2LinkUrl,
  },
  gridItem3 = {
    image: ce11Defaults.gridItem3Image,
    imageAlt: ce11Defaults.gridItem3ImageAlt,
    linkText: ce11Defaults.gridItem3LinkText,
    linkUrl: ce11Defaults.gridItem3LinkUrl,
  },
  gridItem4 = {
    image: ce11Defaults.gridItem4Image,
    imageAlt: ce11Defaults.gridItem4ImageAlt,
    linkText: ce11Defaults.gridItem4LinkText,
    linkUrl: ce11Defaults.gridItem4LinkUrl,
  },
  gridItem5 = {
    image: ce11Defaults.gridItem5Image,
    imageAlt: ce11Defaults.gridItem5ImageAlt,
    linkText: ce11Defaults.gridItem5LinkText,
    linkUrl: ce11Defaults.gridItem5LinkUrl,
  },
  gridItem6 = {
    image: ce11Defaults.gridItem6Image,
    imageAlt: ce11Defaults.gridItem6ImageAlt,
    linkText: ce11Defaults.gridItem6LinkText,
    linkUrl: ce11Defaults.gridItem6LinkUrl,
  },
  gridItem7 = {
    image: ce11Defaults.gridItem7Image,
    imageAlt: ce11Defaults.gridItem7ImageAlt,
    linkText: ce11Defaults.gridItem7LinkText,
    linkUrl: ce11Defaults.gridItem7LinkUrl,
  },
  gridItem8 = {
    image: ce11Defaults.gridItem8Image,
    imageAlt: ce11Defaults.gridItem8ImageAlt,
    linkText: ce11Defaults.gridItem8LinkText,
    linkUrl: ce11Defaults.gridItem8LinkUrl,
  },
  gridItem9 = {
    image: ce11Defaults.gridItem9Image,
    imageAlt: ce11Defaults.gridItem9ImageAlt,
    linkText: ce11Defaults.gridItem9LinkText,
    linkUrl: ce11Defaults.gridItem9LinkUrl,
  },
  className,
  linkRouteDefaults,
}) => {
  const gridItems = [
    gridItem1,
    gridItem2,
    gridItem3,
    gridItem4,
    gridItem5,
    gridItem6,
    gridItem7,
    gridItem8,
    gridItem9,
  ];
  const componentId = useId();
  const containerRef = useRef<HTMLDivElement>(null);
  const rightSectionRef = useRef<HTMLDivElement>(null);
  const eyebrowRef = useRef<HTMLParagraphElement>(null);
  const headingLine1Ref = useRef<HTMLDivElement>(null);
  const headingLine2Ref = useRef<HTMLDivElement>(null);
  const headingLine3Ref = useRef<HTMLDivElement>(null);
  const descriptionRef = useRef<HTMLParagraphElement>(null);
  const buttonRef = useRef<HTMLAnchorElement>(null);

  // Separate refs for mobile heading elements
  const mobileEyebrowRef = useRef<HTMLParagraphElement>(null);
  const mobileHeadingLine1Ref = useRef<HTMLDivElement>(null);
  const mobileHeadingLine2Ref = useRef<HTMLDivElement>(null);
  const mobileHeadingLine3Ref = useRef<HTMLDivElement>(null);
  const mobileDescriptionRef = useRef<HTMLParagraphElement>(null);

  const imageSection1Ref = useRef<HTMLDivElement>(null);
  const imageSection2Ref = useRef<HTMLDivElement>(null);
  const imageSection3Ref = useRef<HTMLDivElement>(null);
  const horizontalScrollRef = useRef<HTMLDivElement>(null);
  const scrollThumbRef = useRef<HTMLDivElement>(null);
  const currentWordIndexRef = useRef<number>(0);
  const [isDesktop, setIsDesktop] = useState(true);

  useEffect(() => {
    const checkScreenSize = () => {
      setIsDesktop(window.innerWidth >= 992);
    };

    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);

    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  useEffect(() => {
    if (
      !eyebrowRef.current ||
      !headingLine1Ref.current ||
      !headingLine2Ref.current ||
      !headingLine3Ref.current ||
      !descriptionRef.current ||
      !buttonRef.current
    )
      return;

    const ctx = gsap.context(() => {
      gsap.set(
        [
          eyebrowRef.current,
          headingLine1Ref.current,
          headingLine3Ref.current,
          descriptionRef.current,
          buttonRef.current,
        ],
        {
          opacity: 0,
        },
      );

      gsap.set(headingLine2Ref.current, {
        opacity: 0,
        color: HIGHLIGHTED_COLORS.word1,
      });

      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: rightSectionRef.current,
          start: "top center+=100px",
          toggleActions: "play none none none",
        },
      });

      tl.fromTo(
        eyebrowRef.current,
        { opacity: 0, y: 20 },
        { opacity: 1, y: 0, duration: 0.6, ease: "power2.out" },
      );

      const line1Text = mainHeading;
      const line1Chars = line1Text.split("");
      if (headingLine1Ref.current) {
        headingLine1Ref.current.textContent = "";
        headingLine1Ref.current.style.opacity = "1";
      }

      const line1Progress = { value: 0 };
      tl.to(
        line1Progress,
        {
          value: 1,
          duration: line1Chars.length * 0.04,
          ease: "none",
          onUpdate: () => {
            const charIndex = Math.floor(line1Progress.value * line1Chars.length);
            const currentText = line1Chars.slice(0, charIndex).join("");
            if (headingLine1Ref.current) {
              headingLine1Ref.current.textContent = currentText;
            }
          },
        },
        "-=0.3",
      );

      const line2Text = highlightedWord1;
      const line2Chars = line2Text.split("");
      if (headingLine2Ref.current) {
        headingLine2Ref.current.textContent = "";
        headingLine2Ref.current.style.opacity = "1";
      }

      const line2Progress = { value: 0 };
      tl.to(
        line2Progress,
        {
          value: 1,
          duration: line2Chars.length * 0.04,
          ease: "none",
          onUpdate: () => {
            const charIndex = Math.floor(line2Progress.value * line2Chars.length);
            const currentText = line2Chars.slice(0, charIndex).join("");
            if (headingLine2Ref.current) {
              headingLine2Ref.current.textContent = currentText;
              headingLine2Ref.current.style.color = HIGHLIGHTED_COLORS.word1;
            }
          },
        },
        "-=0.2",
      );

      const line3Text = subHeading;
      const line3Chars = line3Text.split("");
      if (headingLine3Ref.current) {
        headingLine3Ref.current.textContent = "";
        headingLine3Ref.current.style.opacity = "1";
      }

      const line3Progress = { value: 0 };
      tl.to(
        line3Progress,
        {
          value: 1,
          duration: line3Chars.length * 0.04,
          ease: "none",
          onUpdate: () => {
            const charIndex = Math.floor(line3Progress.value * line3Chars.length);
            const currentText = line3Chars.slice(0, charIndex).join("");
            if (headingLine3Ref.current) {
              headingLine3Ref.current.textContent = currentText;
            }
          },
        },
        "-=0.2",
      );

      tl.fromTo(
        descriptionRef.current,
        { opacity: 0, y: 20 },
        { opacity: 1, y: 0, duration: 0.6, ease: "power2.out" },
        "-=0.5",
      );

      tl.fromTo(
        buttonRef.current,
        { opacity: 0, scale: 0.9 },
        { opacity: 1, scale: 1, duration: 0.5, ease: "back.out(1.4)" },
        "-=0.3",
      );
    });

    return () => ctx.revert();
  }, [mainHeading, highlightedWord1, subHeading]);

  useEffect(() => {
    if (isDesktop && !headingLine2Ref.current) return;
    if (!isDesktop && !mobileHeadingLine2Ref.current) return;

    const words = [highlightedWord1, highlightedWord2, highlightedWord3];
    const colors = [HIGHLIGHTED_COLORS.word1, HIGHLIGHTED_COLORS.word2, HIGHLIGHTED_COLORS.word3];

    const changeWord = (
      newWord: string,
      color: string,
      targetRef: React.RefObject<HTMLDivElement>,
    ) => {
      if (!targetRef.current) return;

      const currentText = targetRef.current.textContent || "";
      const tl = gsap.timeline();

      const eraseProgress = { value: 0 };
      tl.to(eraseProgress, {
        value: 1,
        duration: currentText.length * 0.05,
        ease: "none",
        onUpdate: () => {
          const charsToShow = Math.ceil(currentText.length * (1 - eraseProgress.value));
          if (targetRef.current) {
            targetRef.current.textContent = currentText.slice(0, charsToShow) || "\u200B";
          }
        },
      });

      const newChars = newWord.split("");
      const typeProgress = { value: 0 };
      tl.to(typeProgress, {
        value: 1,
        duration: newChars.length * 0.05,
        ease: "none",
        onUpdate: () => {
          const charIndex = Math.floor(typeProgress.value * newChars.length);
          if (targetRef.current) {
            targetRef.current.textContent = newChars.slice(0, charIndex).join("") || "\u200B";
            targetRef.current.style.color = color;
          }
        },
      });

      return tl;
    };

    if (
      isDesktop &&
      imageSection1Ref.current &&
      imageSection2Ref.current &&
      imageSection3Ref.current
    ) {
      const section1 = imageSection1Ref.current;
      const section2 = imageSection2Ref.current;
      const section3 = imageSection3Ref.current;

      ScrollTrigger.getById(`${componentId}-word-1`)?.kill();
      ScrollTrigger.getById(`${componentId}-word-2`)?.kill();
      ScrollTrigger.getById(`${componentId}-word-3`)?.kill();
      ScrollTrigger.getById(`${componentId}-word-4`)?.kill();

      const getContentBottomTop = () => {
        const wrapper = rightSectionRef.current;
        if (!wrapper) return "top 40%";

        const rootFontSize = Number.parseFloat(getComputedStyle(document.documentElement).fontSize);
        const stickyTopPx = 6 * rootFontSize;

        return `top ${stickyTopPx + wrapper.offsetHeight}px`;
      };

      const getContentBottomBottom = () => {
        const wrapper = rightSectionRef.current;
        if (!wrapper) return "bottom 40%";

        const rootFontSize = Number.parseFloat(getComputedStyle(document.documentElement).fontSize);
        const stickyTopPx = 6 * rootFontSize;

        return `bottom ${stickyTopPx + wrapper.offsetHeight}px`;
      };

      const ctx = gsap.context(() => {
        // Word 1→2: fires when section2 top reaches content bottom (scrolling down)
        ScrollTrigger.create({
          id: `${componentId}-word-1`,
          trigger: section2,
          start: getContentBottomTop,
          invalidateOnRefresh: true,
          onEnter: () => {
            if (currentWordIndexRef.current === 0) {
              changeWord(words[1], colors[1], headingLine2Ref);
              currentWordIndexRef.current = 1;
            }
          },
        });

        // Word 2→1: fires when section1 bottom passes content bottom (scrolling back up)
        ScrollTrigger.create({
          id: `${componentId}-word-2`,
          trigger: section1,
          start: getContentBottomBottom,
          invalidateOnRefresh: true,
          onLeaveBack: () => {
            if (currentWordIndexRef.current === 1) {
              changeWord(words[0], colors[0], headingLine2Ref);
              currentWordIndexRef.current = 0;
            }
          },
        });

        // Word 2→3: fires when section3 top reaches content bottom (scrolling down)
        ScrollTrigger.create({
          id: `${componentId}-word-3`,
          trigger: section3,
          start: getContentBottomTop,
          invalidateOnRefresh: true,
          onEnter: () => {
            if (currentWordIndexRef.current === 1) {
              changeWord(words[2], colors[2], headingLine2Ref);
              currentWordIndexRef.current = 2;
            }
          },
        });

        // Word 3→2: fires when section2 bottom passes content bottom (scrolling back up)
        ScrollTrigger.create({
          id: `${componentId}-word-4`,
          trigger: section2,
          start: getContentBottomBottom,
          invalidateOnRefresh: true,
          onLeaveBack: () => {
            if (currentWordIndexRef.current === 2) {
              changeWord(words[1], colors[1], headingLine2Ref);
              currentWordIndexRef.current = 1;
            }
          },
        });
      });

      const refreshTimeout = setTimeout(() => {
        ScrollTrigger.refresh();
      }, 100);

      const resizeObserver = new ResizeObserver(() => {
        ScrollTrigger.refresh();
      });

      if (containerRef.current?.parentElement) {
        resizeObserver.observe(containerRef.current.parentElement);
      }

      return () => {
        clearTimeout(refreshTimeout);
        resizeObserver.disconnect();
        ctx.revert();
      };
    }

    if (!isDesktop && horizontalScrollRef.current) {
      const scrollContainer = horizontalScrollRef.current;

      currentWordIndexRef.current = 0;

      const updateScrollThumb = () => {
        if (!scrollThumbRef.current || !scrollContainer) return;
        const { scrollLeft, scrollWidth, clientWidth } = scrollContainer;
        const maxScroll = scrollWidth - clientWidth;
        if (maxScroll <= 0) return;
        const progress = scrollLeft / maxScroll;
        const trackWidth = scrollThumbRef.current.parentElement?.offsetWidth || 0;
        const thumbWidth = scrollThumbRef.current.offsetWidth;
        scrollThumbRef.current.style.transform = `translateX(${
          progress * (trackWidth - thumbWidth)
        }px)`;
      };

      updateScrollThumb();

      const handleHorizontalScroll = () => {
        if (!scrollContainer) return;

        updateScrollThumb();

        const cards = scrollContainer.querySelectorAll(`.${styles.mobileImageCard}`);
        if (cards.length < 7) return;

        const containerRect = scrollContainer.getBoundingClientRect();
        const containerCenter = containerRect.left + containerRect.width / 2;

        const card7 = cards[6] as HTMLElement;
        const card7Rect = card7.getBoundingClientRect();
        const card7Center = card7Rect.left + card7Rect.width / 2;

        const card4 = cards[3] as HTMLElement;
        const card4Rect = card4.getBoundingClientRect();
        const card4Center = card4Rect.left + card4Rect.width / 2;

        let newIndex = 0;

        if (card7Center <= containerCenter) {
          newIndex = 2; // Show 3rd text
        } else if (card4Center <= containerCenter) {
          newIndex = 1; // Show 2nd text
        }

        if (newIndex !== currentWordIndexRef.current) {
          changeWord(words[newIndex], colors[newIndex], mobileHeadingLine2Ref);
          currentWordIndexRef.current = newIndex;
        }
      };

      scrollContainer.addEventListener("scroll", handleHorizontalScroll);

      return () => {
        scrollContainer.removeEventListener("scroll", handleHorizontalScroll);
      };
    }
  }, [componentId, highlightedWord1, highlightedWord2, highlightedWord3, isDesktop]);

  return (
    <section className={`${styles.section} ${className || ""}`}>
      <div className={styles.ceContainer}>
        <div className={styles.mobileHeadingSection}>
          <div className={styles.mobileContentWrapper}>
            <p ref={mobileEyebrowRef} className={styles.eyebrowText}>
              {eyebrowText}
            </p>

            <h2 className={styles.heading}>
              <div ref={mobileHeadingLine1Ref} className={styles.headingLine1}>
                {mainHeading}
              </div>
              <div className={styles.heading2Wrapper}>
                <div ref={mobileHeadingLine2Ref} className={styles.headingLine2}>
                  {highlightedWord1}
                </div>
                <div className={styles.verticalLine}></div>
              </div>
              <div ref={mobileHeadingLine3Ref} className={styles.headingLine3}>
                {subHeading}
              </div>
            </h2>

            <p ref={mobileDescriptionRef} className={styles.description}>
              {description}
            </p>
            <LinkWithLang
              {...linkRouteDefaults}
              type={ctaLinkType}
              data={{ code: ctaCategorySlug, url: ctaOtherUrl }}
              target={ctaOpenInNewTab ? "_blank" : "_self"}
              className={styles.primaryBtn}
            >
              {ctaText}
            </LinkWithLang>
          </div>
        </div>

        <div className={styles.mobileImagesSection}>
          <div ref={horizontalScrollRef} className={styles.horizontalScrollContainer}>
            {gridItems.map((item, index) => (
              <div
                key={`${item.linkUrl || item.linkText}-${index}`}
                className={styles.mobileImageCard}
              >
                <GridItemCard
                  item={item}
                  imageWrapperClass={styles.mobileImageWrapper}
                  linkWrapperClass={styles.mobileLinkWrapper}
                  linkRouteDefaults={linkRouteDefaults}
                />
              </div>
            ))}
          </div>
          <div className={styles.scrollTrack}>
            <div ref={scrollThumbRef} className={styles.scrollThumb} />
          </div>
        </div>

        <div ref={containerRef} className={styles.desktopLayout}>
          <div className={styles.leftSection}>
            <ul className={styles.gridList}>
              <div ref={imageSection1Ref} className={styles.gridRow}>
                <li className={styles.gridItemLarge}>
                  <GridItemCard
                    item={gridItems[0]}
                    imageWrapperClass={styles.desktopImageWrapper}
                    linkWrapperClass={styles.desktopLinkWrapperTop}
                    linkRouteDefaults={linkRouteDefaults}
                  />
                </li>
                <div className={styles.gridStack}>
                  <li className={styles.gridItemTop}>
                    <GridItemCard
                      item={gridItems[1]}
                      imageWrapperClass={styles.desktopImageWrapper}
                      linkWrapperClass={styles.desktopLinkWrapperBottom}
                      linkRouteDefaults={linkRouteDefaults}
                    />
                  </li>

                  <li className={styles.gridItemBottom}>
                    <GridItemCard
                      item={gridItems[2]}
                      imageWrapperClass={styles.desktopImageWrapper}
                      linkWrapperClass={styles.desktopLinkWrapperBottom}
                      linkRouteDefaults={linkRouteDefaults}
                    />
                  </li>
                </div>
              </div>

              <div ref={imageSection2Ref} className={styles.gridRow}>
                <div className={styles.gridStack}>
                  <li className={styles.gridItemEqual}>
                    <GridItemCard
                      item={gridItems[3]}
                      imageWrapperClass={styles.desktopImageWrapper}
                      linkWrapperClass={styles.desktopLinkWrapperBottom}
                      linkRouteDefaults={linkRouteDefaults}
                    />
                  </li>

                  <li className={styles.gridItemEqual}>
                    <GridItemCard
                      item={gridItems[4]}
                      imageWrapperClass={styles.desktopImageWrapper}
                      linkWrapperClass={styles.desktopLinkWrapperBottom}
                      linkRouteDefaults={linkRouteDefaults}
                    />
                  </li>
                </div>

                <li className={styles.gridItemMedium}>
                  <GridItemCard
                    item={gridItems[5]}
                    imageWrapperClass={styles.desktopImageWrapper}
                    linkWrapperClass={styles.desktopLinkWrapperBottom}
                    linkRouteDefaults={linkRouteDefaults}
                  />
                </li>
              </div>

              <div ref={imageSection3Ref} className={styles.gridRow}>
                <li className={styles.gridItemMedium}>
                  <GridItemCard
                    item={gridItems[6]}
                    imageWrapperClass={styles.desktopImageWrapper}
                    linkWrapperClass={styles.desktopLinkWrapperBottom}
                    linkRouteDefaults={linkRouteDefaults}
                  />
                </li>

                <div className={styles.gridStack}>
                  <li className={styles.gridItemEqual}>
                    <GridItemCard
                      item={gridItems[7]}
                      imageWrapperClass={styles.desktopImageWrapper}
                      linkWrapperClass={styles.desktopLinkWrapperBottom}
                      linkRouteDefaults={linkRouteDefaults}
                    />
                  </li>

                  <li className={styles.gridItemEqual}>
                    <GridItemCard
                      item={gridItems[8]}
                      imageWrapperClass={styles.desktopImageWrapper}
                      linkWrapperClass={styles.desktopLinkWrapperBottom}
                      linkRouteDefaults={linkRouteDefaults}
                    />
                  </li>
                </div>
              </div>
            </ul>
          </div>

          <div className={styles.rightSection}>
            <div ref={rightSectionRef} className={styles.rightContentWrapper}>
              <div className={styles.desktopHeadingWrapper}>
                <p ref={eyebrowRef} className={styles.eyebrowText}>
                  {eyebrowText}
                </p>
                <div>
                  <h2 className={styles.heading}>
                    <div ref={headingLine1Ref} className={styles.headingLine1}>
                      {mainHeading}
                    </div>
                    <div className={styles.heading2Wrapper}>
                      <div ref={headingLine2Ref} className={styles.headingLine2}>
                        {highlightedWord1}
                      </div>
                      <div className={styles.verticalLine}></div>
                    </div>
                    <div ref={headingLine3Ref} className={styles.headingLine3}>
                      {subHeading}
                    </div>
                  </h2>
                </div>
                <p ref={descriptionRef} className={styles.description}>
                  {description}
                </p>
                <LinkWithLang
                  {...linkRouteDefaults}
                  type={ctaLinkType}
                  data={{ code: ctaCategorySlug, url: ctaOtherUrl }}
                  target={ctaOpenInNewTab ? "_blank" : "_self"}
                  className={styles.primaryBtn}
                >
                  {ctaText}
                </LinkWithLang>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CE11;
